import {
  getV1CompaniesCompanyIdBillingLines,
  putV1CompaniesCompanyIdAddOnChargesIdReject,
  putV1CompaniesCompanyIdAddOnChargesIdUnreject,
  putV1CompaniesCompanyIdInvoiceLineItemsIdReject,
  putV1CompaniesCompanyIdInvoiceLineItemsIdUnreject,
  RejectedReason,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import connection from '~services/connectionModule';
import { isConflictError } from '~utils/extractAxiosError';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { BillingLine } from './models';

// The caller shows the API's message for a 409, which says which settlement holds the line
function throwIfLineSettled(error: unknown) {
  if (isConflictError(error)) {
    throw error;
  }
}

function requireLineId(line: BillingLine) {
  if (!line.id) {
    throw new Error('A billing line needs an id to be rejected or un-rejected');
  }

  return line.id;
}

export default function useBillingLines() {
  // The caller totals every line of each job, so it needs every page. None render as rows.
  const fetchAllBillingLinesForJobs = useCallback(
    async (companyId: string, jobIds: string[], signal?: AbortSignal) => {
      // With no ids the API would read every line of the company
      if (jobIds.length === 0) {
        return [];
      }

      try {
        return await fetchAllApiPages(
          (pageQuery, pageSignal) =>
            getV1CompaniesCompanyIdBillingLines({
              path: { 'company-id': companyId },
              query: { job_ids: jobIds, ...pageQuery },
              signal: pageSignal,
            }),
          BillingLine.parse,
          { signal },
        );
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.billing_lines.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const rejectBillingLine = useCallback(
    async (companyId: string, line: BillingLine, reason: RejectedReason) => {
      try {
        const path = { 'company-id': companyId, id: requireLineId(line) };

        if (line.addOnCharge) {
          await putV1CompaniesCompanyIdAddOnChargesIdReject({ path, body: { reason } });
        } else {
          await putV1CompaniesCompanyIdInvoiceLineItemsIdReject({
            path,
            body: { reason },
          });
        }
      } catch (error) {
        throwIfLineSettled(error);
        connection.handleRequestError(
          error,
          t('error_messages.billing_lines.failed_to_reject'),
        );
        throw error;
      }
    },
    [],
  );

  const unrejectBillingLine = useCallback(
    async (companyId: string, line: BillingLine) => {
      try {
        const path = { 'company-id': companyId, id: requireLineId(line) };

        if (line.addOnCharge) {
          await putV1CompaniesCompanyIdAddOnChargesIdUnreject({ path });
        } else {
          await putV1CompaniesCompanyIdInvoiceLineItemsIdUnreject({ path });
        }
      } catch (error) {
        throwIfLineSettled(error);
        connection.handleRequestError(
          error,
          t('error_messages.billing_lines.failed_to_unreject'),
        );
        throw error;
      }
    },
    [],
  );

  return { fetchAllBillingLinesForJobs, rejectBillingLine, unrejectBillingLine };
}
